import { api } from '@/lib/api';
import type {
  FunnelResponse,
  CreateFunnelRequest,
  UpdateFunnelRequest,
  FunnelQuery,
  FunnelListResponse,
  ReorderFunnelsRequest,
  FunnelDeleteValidation,
  DeleteFunnelRequest,
  DeleteFunnelResult,
  FunnelStepResponse,
  CreateFunnelStepRequest,
  UpdateFunnelStepRequest,
  FunnelStepQuery,
  FunnelStepListResponse,
  ReorderFunnelStepsRequest,
  FunnelStepDeleteValidation
} from '@troia-v3/types';

/**
 * Funnels API Service
 * Handles all funnel-related API calls
 */
export const funnelsApi = {
  // ============================================================================
  // FUNNELS CRUD
  // ============================================================================

  /**
   * Get all funnels with pagination and filters
   */
  async getFunnels(query?: FunnelQuery): Promise<FunnelListResponse> {
    const response = await api.get<{ success: boolean; data: FunnelListResponse }>('/funnels', {
      params: query
    });
    return response.data.data;
  },

  /**
   * Get funnel by ID
   */
  async getFunnelById(id: string): Promise<FunnelResponse> {
    const response = await api.get<{ success: boolean; data: FunnelResponse }>(`/funnels/${id}`);
    return response.data.data;
  },

  /**
   * Get funnel by ID for dropdown (no module permissions required)
   */
  async getFunnelByIdForDropdown(id: string): Promise<FunnelResponse> {
    const response = await api.get<{ success: boolean; data: FunnelResponse }>(`/funnels/dropdown/${id}`);
    return response.data.data;
  },

  /**
   * Create new funnel
   */
  async createFunnel(data: CreateFunnelRequest): Promise<FunnelResponse> {
    const response = await api.post<{ success: boolean; data: FunnelResponse }>('/funnels', data);
    return response.data.data;
  },

  /**
   * Update funnel
   */
  async updateFunnel(id: string, data: UpdateFunnelRequest): Promise<FunnelResponse> {
    const response = await api.put<{ success: boolean; data: FunnelResponse }>(`/funnels/${id}`, data);
    return response.data.data;
  },

  /**
   * Exclui o funil, opcionalmente decidindo o destino dos leads vinculados.
   *
   * - sem `body` → comportamento histórico: funil COM leads é recusado com 400
   * - `{ leads: { action: 'delete' } }` → exclui os leads junto
   * - `{ leads: { action: 'transfer', funnelId, stepId } }` → move os leads
   *   para o funil/etapa informados (o backend recusa etapa de outro funil)
   *
   * O corpo vai em `data` porque o axios não manda payload em DELETE de outra
   * forma. Ver contrato em `.superpowers/sdd/2026-08-26-fase-c2-.../task-1-report.md`.
   */
  async deleteFunnel(id: string, body?: DeleteFunnelRequest): Promise<DeleteFunnelResult> {
    const response = await api.delete<{ success: boolean; data: DeleteFunnelResult }>(
      `/funnels/${id}`,
      body ? { data: body } : undefined,
    );
    return response.data.data;
  },

  /**
   * Validate if funnel can be deleted
   */
  async validateDeleteFunnel(id: string): Promise<FunnelDeleteValidation> {
    const response = await api.get<{ success: boolean; data: FunnelDeleteValidation }>(`/funnels/validate-delete/${id}`);
    return response.data.data;
  },

  /**
   * Delete funnel with validation
   */
  async deleteFunnelWithValidation(id: string): Promise<void> {
    await api.delete(`/funnels/${id}/with-validation`);
  },

  /**
   * Reorder funnels
   */
  async reorderFunnels(data: ReorderFunnelsRequest): Promise<FunnelResponse[]> {
    const response = await api.post<{ success: boolean; data: FunnelResponse[] }>('/funnels/reorder', data);
    return response.data.data;
  },

  /**
   * Get funnels for dropdown (active only)
   */
  async getFunnelsForDropdown(): Promise<FunnelResponse[]> {
    const response = await api.get<{ success: boolean; data: FunnelResponse[] }>('/funnels/dropdown');
    return response.data.data;
  },

  /**
   * Get active funnels
   */
  async getActiveFunnels(): Promise<FunnelResponse[]> {
    const response = await api.get<{ success: boolean; data: FunnelResponse[] }>('/funnels/active');
    return response.data.data;
  },

  // ============================================================================
  // FUNNEL STEPS CRUD
  // ============================================================================

  /**
   * Get all funnel steps with pagination and filters
   */
  async getFunnelSteps(query?: FunnelStepQuery): Promise<FunnelStepListResponse> {
    const response = await api.get<{ success: boolean; data: FunnelStepListResponse }>('/funnel-steps', {
      params: query
    });
    return response.data.data;
  },

  /**
   * Get funnel step by ID
   */
  async getFunnelStepById(id: string): Promise<FunnelStepResponse> {
    const response = await api.get<{ success: boolean; data: FunnelStepResponse }>(`/funnel-steps/${id}`);
    return response.data.data;
  },

  /**
   * Get steps for specific funnel
   */
  async getStepsByFunnelId(funnelId: string): Promise<FunnelStepResponse[]> {
    const response = await api.get<{ success: boolean; data: FunnelStepResponse[] }>(`/funnel-steps/by-funnel/${funnelId}`);
    return response.data.data;
  },

  /**
   * Create new funnel step
   */
  async createFunnelStep(data: CreateFunnelStepRequest): Promise<FunnelStepResponse> {
    const response = await api.post<{ success: boolean; data: FunnelStepResponse }>('/funnel-steps', data);
    return response.data.data;
  },

  /**
   * Update funnel step
   */
  async updateFunnelStep(id: string, data: UpdateFunnelStepRequest): Promise<FunnelStepResponse> {
    const response = await api.put<{ success: boolean; data: FunnelStepResponse }>(`/funnel-steps/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete funnel step
   */
  async deleteFunnelStep(id: string): Promise<void> {
    await api.delete(`/funnel-steps/${id}`);
  },

  /**
   * Validate if funnel step can be deleted
   */
  async validateDeleteFunnelStep(id: string): Promise<FunnelStepDeleteValidation> {
    const response = await api.get<{ success: boolean; data: FunnelStepDeleteValidation }>(`/funnel-steps/validate-delete/${id}`);
    return response.data.data;
  },

  /**
   * Delete funnel step with validation
   */
  async deleteFunnelStepWithValidation(id: string): Promise<void> {
    await api.delete(`/funnel-steps/${id}/with-validation`);
  },

  /**
   * Reorder funnel steps
   */
  async reorderFunnelSteps(data: ReorderFunnelStepsRequest): Promise<FunnelStepResponse[]> {
    const response = await api.post<{ success: boolean; data: FunnelStepResponse[] }>('/funnel-steps/reorder', data);
    return response.data.data;
  },

  /**
   * Activate funnel step
   */
  async activateFunnelStep(id: string): Promise<FunnelStepResponse> {
    const response = await api.patch<{ success: boolean; data: FunnelStepResponse }>(`/funnel-steps/${id}/activate`);
    return response.data.data;
  },

  /**
   * Deactivate funnel step
   */
  async deactivateFunnelStep(id: string): Promise<FunnelStepResponse> {
    const response = await api.patch<{ success: boolean; data: FunnelStepResponse }>(`/funnel-steps/${id}/deactivate`);
    return response.data.data;
  }
};
